import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  MergeCellsOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { apiClient } from "../../../services/api/apiClient";
import { PageHeader } from "../../../components/common/PageHeader";
import { usePageTitle } from "../../../hooks";

const { Text } = Typography;

interface DecreaseReason {
  id: number;
  name: string;
  english_name: string;
  name_it: string;
  name_pt: string;
  assignment_count: number;
}

interface DecreaseReasonFormValues {
  name: string;
  english_name?: string;
  name_it?: string;
  name_pt?: string;
}

/**
 * Admin page for `DecreaseReason` — catálogo de motivos de merma
 * usados por `AuditPlot.decrease_reason` (FK) y por la M2M
 * `Audit.reason_for_decrease`.
 *
 * El backend uppercasea `name` al guardar (mirroring del legacy admin
 * Django), así que la UI muestra los nombres en mayúsculas.
 */
export const DecreaseReasonsList = () => {
  const { t } = useTranslation();
  const { message: notify } = AntdApp.useApp();
  const [data, setData] = useState<DecreaseReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<DecreaseReason | null>(null);
  const [form] = Form.useForm<DecreaseReasonFormValues>();
  const [saving, setSaving] = useState(false);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTargetId, setMergeTargetId] = useState<number | null>(null);
  const [merging, setMerging] = useState(false);

  usePageTitle(
    t("decrease_reasons.title", "Motivos de merma"),
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiClient.get<DecreaseReason[]>("/admin/decrease-reasons/");
      const list = Array.isArray(r.data)
        ? r.data
        : ((r.data as any).results ?? []);
      setData(list);
    } catch {
      notify.error(
        t("decrease_reasons.load_error", "Error al cargar los motivos de merma"),
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [notify, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setEditorOpen(true);
  };

  const openEdit = (row: DecreaseReason) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      english_name: row.english_name ?? "",
      name_it: row.name_it ?? "",
      name_pt: row.name_pt ?? "",
    });
    setEditorOpen(true);
  };

  const submitEditor = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      if (editing) {
        await apiClient.patch(`/admin/decrease-reasons/${editing.id}/`, values);
        notify.success(t("decrease_reasons.update_success", "Motivo actualizado"));
      } else {
        await apiClient.post("/admin/decrease-reasons/", values);
        notify.success(t("decrease_reasons.create_success", "Motivo creado"));
      }
      setEditorOpen(false);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        e?.response?.data?.name?.[0] ??
        t("decrease_reasons.save_error", "No se pudo guardar el motivo");
      notify.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const deleteReason = async (row: DecreaseReason) => {
    try {
      await apiClient.delete(`/admin/decrease-reasons/${row.id}/`);
      notify.success(t("decrease_reasons.delete_success", "Motivo eliminado"));
      setSelectedRowKeys((keys) => keys.filter((k) => k !== row.id));
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("decrease_reasons.delete_error", "No se pudo eliminar el motivo");
      notify.error(msg);
    }
  };

  const selectedRows = useMemo(
    () => data.filter((r) => selectedRowKeys.includes(r.id)),
    [data, selectedRowKeys],
  );

  const openMerge = () => {
    if (selectedRowKeys.length < 2) {
      notify.warning(
        t(
          "decrease_reasons.merge_need_two",
          "Selecciona al menos dos motivos para fusionar.",
        ),
      );
      return;
    }
    const heaviest = [...selectedRows].sort(
      (a, b) => b.assignment_count - a.assignment_count,
    )[0];
    setMergeTargetId(heaviest?.id ?? selectedRowKeys[0]);
    setMergeOpen(true);
  };

  const submitMerge = async () => {
    if (!mergeTargetId) return;
    const sources = selectedRowKeys.filter((k) => k !== mergeTargetId);
    if (sources.length === 0) {
      notify.warning(
        t(
          "decrease_reasons.merge_no_sources",
          "Selecciona al menos un motivo distinto del destino.",
        ),
      );
      return;
    }
    setMerging(true);
    try {
      const res = await apiClient.post<{
        moved_audit_plots: number;
        moved_audit_m2m: number;
      }>("/admin/decrease-reasons/merge/", {
        target_id: mergeTargetId,
        source_ids: sources,
      });
      const moved =
        (res.data?.moved_audit_plots ?? 0) + (res.data?.moved_audit_m2m ?? 0);
      notify.success(
        t(
          "decrease_reasons.merge_success",
          "Fusionados {{n}} motivos · {{moved}} referencias reasignadas",
          { n: sources.length, moved },
        ),
      );
      setMergeOpen(false);
      setSelectedRowKeys([]);
      await load();
    } catch (e: any) {
      const msg =
        e?.response?.data?.detail ??
        t("decrease_reasons.merge_error", "No se pudo fusionar");
      notify.error(msg);
    } finally {
      setMerging(false);
    }
  };

  const columns = [
    {
      title: t("decrease_reasons.name", "Nombre"),
      dataIndex: "name",
      key: "name",
      ellipsis: { showTitle: false } as any,
      render: (v: string) => (
        <Tooltip title={v} placement="topLeft" mouseEnterDelay={0.25}>
          <strong>{v}</strong>
        </Tooltip>
      ),
      sorter: (a: DecreaseReason, b: DecreaseReason) =>
        a.name.localeCompare(b.name),
    },
    {
      title: t("decrease_reasons.translations", "Traducciones"),
      key: "translations",
      width: 220,
      render: (_: any, row: DecreaseReason) => {
        const entries: Array<{ code: string; label: string; value: string }> = [
          { code: "EN", label: "English", value: row.english_name },
          { code: "IT", label: "Italiano", value: row.name_it },
          { code: "PT", label: "Português", value: row.name_pt },
        ];
        return (
          <Space size={4} wrap>
            {entries.map((e) =>
              e.value ? (
                <Tooltip key={e.code} title={`${e.label}: ${e.value}`}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ) : (
                <Tooltip
                  key={e.code}
                  title={`${e.label}: ${t(
                    "decrease_reasons.no_translation",
                    "sin traducir",
                  )}`}
                >
                  <Tag color="default" style={{ margin: 0, opacity: 0.55 }}>
                    {e.code}
                  </Tag>
                </Tooltip>
              ),
            )}
          </Space>
        );
      },
    },
    {
      title: t("decrease_reasons.assignment_count", "Auditorías/parcelas"),
      dataIndex: "assignment_count",
      key: "assignment_count",
      width: 180,
      align: "center" as const,
      sorter: (a: DecreaseReason, b: DecreaseReason) =>
        a.assignment_count - b.assignment_count,
      render: (n: number) =>
        n > 0 ? <Tag color="orange">{n}</Tag> : <Tag color="default">0</Tag>,
    },
    {
      title: t("common.actions", "Acciones"),
      key: "actions",
      width: 160,
      align: "right" as const,
      render: (_: any, row: DecreaseReason) => (
        <Space size="small">
          <Tooltip title={t("common.edit", "Editar")}>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            />
          </Tooltip>
          <Popconfirm
            title={t("decrease_reasons.delete_confirm", "¿Eliminar este motivo?")}
            description={
              row.assignment_count > 0
                ? t(
                    "decrease_reasons.delete_blocked",
                    "No puedes eliminar un motivo asignado. Fusiónalo primero.",
                  )
                : t(
                    "decrease_reasons.delete_warning",
                    "Esta acción no se puede deshacer.",
                  )
            }
            onConfirm={() => deleteReason(row)}
            okText={t("common.yes", "Sí")}
            cancelText={t("common.no", "No")}
            okButtonProps={{ danger: true }}
            disabled={row.assignment_count > 0}
          >
            <Tooltip
              title={
                row.assignment_count > 0
                  ? t(
                      "decrease_reasons.delete_blocked_tooltip",
                      "Tiene auditorías o parcelas asignadas — no se puede eliminar",
                    )
                  : t("common.delete", "Eliminar")
              }
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={row.assignment_count > 0}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card>
      <PageHeader
        icon={<WarningOutlined />}
        accent="#D46B08"
        title={t("decrease_reasons.title", "Motivos de merma")}
        subtitle={t(
          "decrease_reasons.subtitle",
          "Catálogo de motivos por los que se registra merma en una auditoría o parcela. Fusiona los duplicados para mantener la lista limpia.",
        )}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={() => void load()}>
              {t("common.reload", "Refrescar")}
            </Button>
            <Tooltip
              title={
                selectedRowKeys.length < 2
                  ? t(
                      "decrease_reasons.merge_hint_tooltip",
                      "Marca las casillas de 2 o más motivos en la tabla para poder fusionarlos",
                    )
                  : ""
              }
            >
              <Button
                icon={<MergeCellsOutlined />}
                onClick={openMerge}
                type={selectedRowKeys.length >= 2 ? "primary" : "default"}
              >
                {t("decrease_reasons.merge", "Fusionar")}
                {selectedRowKeys.length > 0 && ` (${selectedRowKeys.length})`}
              </Button>
            </Tooltip>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("decrease_reasons.create", "Nuevo motivo")}
            </Button>
          </Space>
        }
      />

      {selectedRowKeys.length < 2 && (
        <Alert
          showIcon
          type="info"
          style={{ marginBottom: 12 }}
          message={t(
            "decrease_reasons.merge_banner",
            "Cómo fusionar: marca las casillas (☐) de los motivos duplicados en la tabla y pulsa Fusionar. Podrás elegir cuál mantener y las auditorías/parcelas del resto se reasignarán automáticamente.",
          )}
        />
      )}

      <Table<DecreaseReason>
        dataSource={data}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        scroll={{ x: 960 }}
        rowSelection={{
          type: "checkbox",
          selectedRowKeys,
          onChange: (keys) => setSelectedRowKeys(keys as number[]),
          getCheckboxProps: () => ({ disabled: false }),
          columnWidth: 48,
          fixed: "left",
        }}
      />

      <Modal
        title={
          editing
            ? t("decrease_reasons.edit_title", "Editar motivo")
            : t("decrease_reasons.create_title", "Nuevo motivo")
        }
        open={editorOpen}
        onCancel={() => (saving ? undefined : setEditorOpen(false))}
        onOk={submitEditor}
        confirmLoading={saving}
        okText={t("common.save", "Guardar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        width={520}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t("decrease_reasons.name_es", "Nombre (Español)")}
            name="name"
            rules={[
              {
                required: true,
                message: t(
                  "decrease_reasons.name_required",
                  "El nombre es obligatorio",
                ),
              },
              { max: 100 },
            ]}
            extra={t(
              "decrease_reasons.name_es_hint",
              "Idioma principal del catálogo — siempre obligatorio. Se guardará automáticamente en mayúsculas.",
            )}
          >
            <Input
              maxLength={100}
              placeholder={t("decrease_reasons.name_placeholder", "Ej: Granizo")}
              autoFocus
            />
          </Form.Item>

          <Text
            type="secondary"
            style={{
              fontSize: 12,
              display: "block",
              marginTop: 4,
              marginBottom: 8,
            }}
          >
            {t(
              "decrease_reasons.translations_hint",
              "Traducciones opcionales — el móvil las usa para pintar el motivo en el idioma del auditor.",
            )}
          </Text>

          <Form.Item
            label={t("decrease_reasons.name_en", "Nombre (English)")}
            name="english_name"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t(
                "decrease_reasons.name_en_placeholder",
                "e.g. Hail",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("decrease_reasons.name_it", "Nombre (Italiano)")}
            name="name_it"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t(
                "decrease_reasons.name_it_placeholder",
                "es. Grandine",
              )}
            />
          </Form.Item>

          <Form.Item
            label={t("decrease_reasons.name_pt", "Nombre (Português)")}
            name="name_pt"
            rules={[{ max: 100 }]}
          >
            <Input
              maxLength={100}
              placeholder={t(
                "decrease_reasons.name_pt_placeholder",
                "ex.: Granizo",
              )}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t("decrease_reasons.merge_title", "Fusionar motivos de merma")}
        open={mergeOpen}
        onCancel={() => (merging ? undefined : setMergeOpen(false))}
        onOk={submitMerge}
        confirmLoading={merging}
        okText={t("decrease_reasons.merge_apply", "Fusionar")}
        cancelText={t("common.cancel", "Cancelar")}
        destroyOnHidden
        okButtonProps={{ danger: true }}
        width={520}
      >
        <Text type="secondary">
          {t(
            "decrease_reasons.merge_hint",
            "Selecciona el motivo que quieres mantener. Los demás se eliminarán y todas sus auditorías y parcelas se reasignarán al motivo elegido.",
          )}
        </Text>
        <div style={{ marginTop: 12 }}>
          <Radio.Group
            value={mergeTargetId}
            onChange={(e) => setMergeTargetId(e.target.value)}
          >
            <Space direction="vertical">
              {selectedRows.map((row) => (
                <Radio key={row.id} value={row.id}>
                  <strong>{row.name}</strong>{" "}
                  <Text type="secondary">
                    ({row.assignment_count}{" "}
                    {row.assignment_count === 1
                      ? t("decrease_reasons.ref_one", "referencia")
                      : t("decrease_reasons.ref_many", "referencias")})
                  </Text>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      </Modal>
    </Card>
  );
};

export default DecreaseReasonsList;
